'use client';

import type { ReactNode } from 'react';

import {
  CalendarDays,
  GraduationCap,
  Shapes,
  Target,
  Trophy,
  Users,
  UsersRound,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import { isGenericClassType } from '../_lib/hooks/use-booking-reducer';
import { BookingChipIcon } from './booking-chip-icon';
import styles from './native-booking-controls.module.css';

interface BookingTypeToggleProps {
  value: string;
  onChange: (value: string) => void;
  availableTypes: string[];
  /**
   * When true, prepends a full-width "All Classes" pill (synthetic value `'all'`)
   * and hides `group_classes`. Used for studios where the booking flow is
   * class-only and the per-booking-type axis is meaningless.
   */
  unifiedClassMode?: boolean;
}

/** Synthetic booking-type slug used by the unified class filter. */
export const ALL_CLASSES_BOOKING_TYPE = 'all';

type IconComponent = typeof Users;

interface BookingTypeConfig {
  /** `null` renders the label with no icon (e.g. the generic space/court type). */
  icon: string | IconComponent | null;
  labelKey: string;
}

/** Known booking type configs with icons and translation keys */
const KNOWN_BOOKING_TYPE_CONFIG: Record<string, BookingTypeConfig> = {
  court: {
    // Court icon shows for sports tenants ("Court Only"). For tenants that
    // relabel this type away from "court" (e.g. member clubs → "Space Only"),
    // the court glyph would be misleading, so it's hidden at render time when
    // the resolved label no longer mentions "court" — see hideCourtIcon below.
    icon: '/images/book/court.svg',
    labelKey: 'bookings:courtOnly',
  },
  trainer: {
    icon: Users,
    labelKey: 'bookings:coaching',
  },
  ball_machine: {
    icon: Target,
    labelKey: 'bookings:ballMachine',
  },
  group_classes: {
    icon: UsersRound,
    labelKey: 'bookings:groupClasses',
  },
  kids_academy: {
    icon: GraduationCap,
    labelKey: 'bookings:kidsAcademy',
  },
  event: {
    icon: CalendarDays,
    labelKey: 'bookings:event',
  },
  tournament: {
    icon: Trophy,
    labelKey: 'bookings:tournament',
  },
};

/** Convert a slug like 'other_custom' to 'Other Custom' */
function slugToLabel(slug: string): string {
  return slug
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Resolve a booking-type slug to the icon element + i18n key used by the pill
 * row. Exported so the single-sport-studio path in booking-form can render a
 * category pill (e.g. "Event") inside SportFilterToggle with the same glyph
 * and label this toggle would have used — keeping one source of truth for the
 * slug → icon/label mapping. `t` is passed in so the caller controls
 * translation (this helper stays render-context free).
 */
export function resolveBookingTypeChip(
  type: string,
  t: (key: string) => string,
): { icon: ReactNode; label: string } {
  const config = KNOWN_BOOKING_TYPE_CONFIG[type];
  const icon = config ? config.icon : Shapes;
  const label = config?.labelKey ? t(config.labelKey) : slugToLabel(type);

  if (icon === null) {
    return { icon: null, label };
  }

  if (typeof icon === 'string') {
    return {
      icon: <BookingChipIcon src={icon} alt={label} />,
      label,
    };
  }

  const Icon = icon;
  return { icon: <Icon className="h-4 w-4 flex-shrink-0" />, label };
}

export function BookingTypeToggle({
  value,
  onChange,
  availableTypes,
  unifiedClassMode = false,
}: BookingTypeToggleProps) {
  const { t } = useTranslation('bookings');

  // In unified-class mode, drop the generic class types (group_classes,
  // semi_private) from the per-template row — their sessions get rolled into
  // "All Classes" (rendered by SportFilterToggle). A studio whose standard
  // class is semi_private must fold the same way group_classes does, or it
  // strands every class under a standalone "Semi Private" pill.
  const visibleTypes = unifiedClassMode
    ? availableTypes.filter((type) => !isGenericClassType(type))
    : availableTypes;

  // In unified mode the "All Classes" pill lives inside SportFilterToggle so
  // it shares one container with the sport row. If nothing remains here, this
  // toggle has nothing to render.
  if (visibleTypes.length === 0) {
    return null;
  }

  const showGrid = visibleTypes.length > 3;

  return (
    <div
      className={cn(
        'bg-muted/30 w-full gap-1 rounded-lg border p-1',
        styles.typeToggle,
        showGrid ? 'grid grid-cols-3' : 'flex',
      )}
    >
      {visibleTypes.map((type) => {
        const config = KNOWN_BOOKING_TYPE_CONFIG[type];
        // Known config keeps its icon (including an intentional `null`); unknown
        // types fall back to the generic Shapes glyph.
        let icon = config ? config.icon : Shapes;
        const label = config?.labelKey ? t(config.labelKey) : slugToLabel(type);

        // The court icon only makes sense when the label is still about a court.
        // Tenants that relabel this type (e.g. member clubs → "Space Only" /
        // "مساحة فقط") get no icon rather than a misleading tennis-court glyph.
        // Matched against the court term in each shipped locale so the icon
        // stays for Arabic sports tenants ("ملعب فقط") too.
        const lowerLabel = label.toLowerCase();
        const labelIsCourt =
          lowerLabel.includes('court') || label.includes('ملعب');
        if (type === 'court' && !labelIsCourt) {
          icon = null;
        }

        const isImageIcon = typeof icon === 'string';

        return (
          <button
            key={type}
            type="button"
            aria-pressed={value === type}
            onClick={() => value !== type && onChange(type)}
            className={cn(
              'flex flex-row items-center justify-center gap-1 rounded-md border px-1 py-2 text-xs font-medium transition-all md:gap-2 md:px-3 md:text-sm',
              styles.typeOption,
              !showGrid && 'flex-1',
              value === type
                ? 'border-primary/35 bg-primary/5 text-foreground shadow-none'
                : 'border-border bg-background text-foreground hover:border-primary/40 shadow-sm hover:shadow-md',
            )}
          >
            {icon === null ? null : isImageIcon ? (
              <BookingChipIcon src={icon as string} alt={label} />
            ) : (
              (() => {
                const Icon = icon as IconComponent;
                return <Icon className="h-4 w-4 flex-shrink-0" />;
              })()
            )}
            <span className="text-center leading-tight">{label}</span>
          </button>
        );
      })}
    </div>
  );
}
